import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Copy, ArrowRight, Smartphone, Bookmark, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const TicketSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const eventKey = searchParams.get('event') || 'live_stream';
  const guestToken = searchParams.get('guest') || '';
  const [copied, setCopied] = useState(false);

  const isExpiredLiveTicket = eventKey === 'live_stream';

  // Persist guest token to localStorage so /events unlocks the stream
  // (NOT for live_stream — that's now subscribers-only and old magic links are invalid)
  useEffect(() => {
    if (guestToken && eventKey && eventKey !== 'live_stream') {
      const guestTickets = JSON.parse(localStorage.getItem('guestTickets') || '{}');
      guestTickets[eventKey] = guestToken;
      localStorage.setItem('guestTickets', JSON.stringify(guestTickets));
    }
  }, [guestToken, eventKey]);

  const watchUrl = `${window.location.origin}/events?ticket=success&event=${eventKey}&guest=${guestToken}`;
  const watchPath = `/events?ticket=success&event=${eventKey}&guest=${guestToken}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(watchUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Expired-live-ticket notice */}
        {isExpiredLiveTicket && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-6 mb-8 text-center">
            <AlertCircle className="w-12 h-12 text-orange-600 mx-auto mb-3" />
            <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
              This Ticket Has Expired
            </h1>
            <p className="text-orange-900 mt-3 max-w-md mx-auto">
              Live stream tickets were valid only for the day of the broadcast.
              Live events are now exclusive to active subscribers.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/subscription">
                <Button size="lg" className="rounded-full px-8 font-bold uppercase tracking-wider">
                  Subscribe to Watch Live
                </Button>
              </Link>
              <Link to="/events">
                <Button size="lg" variant="outline" className="rounded-full px-8">
                  Browse Events
                </Button>
              </Link>
            </div>
          </div>
        )}

        {!isExpiredLiveTicket && (
          <>
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Check className="w-12 h-12 text-white" strokeWidth={3} />
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">
            Ticket Confirmed
          </h1>
          <p className="text-muted-foreground mt-3 text-lg">
            Payment successful — you're in.
          </p>
        </div>

        {/* Big watch button */}
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-1 shadow-2xl mb-6">
          <Link to={watchPath} className="block bg-background rounded-xl p-6 sm:p-8 text-center">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-bold">Your ticket is ready</p>
            <Button size="lg" className="rounded-full px-8 py-6 text-lg font-bold uppercase tracking-wider" data-testid="watch-now-btn">
              Watch the Live Event Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Magic link */}
        <div className="bg-card border-2 border-primary/30 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <Bookmark className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-display font-bold text-xl uppercase tracking-tight">Save This Link</h2>
              <p className="text-sm text-muted-foreground mt-1">
                This is YOUR ticket. Open it from any phone or computer to watch — works on every device.
              </p>
            </div>
          </div>

          <div className="bg-muted/40 border border-border rounded-lg p-3 break-all text-sm font-mono mb-3" data-testid="magic-link-text">
            {watchUrl}
          </div>

          <Button
            onClick={copy}
            variant={copied ? 'secondary' : 'default'}
            className="w-full font-bold uppercase tracking-wider"
            data-testid="copy-link-btn"
          >
            {copied ? (
              <><Check className="w-4 h-4 mr-2" /> Copied!</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" /> Copy Link</>
            )}
          </Button>
        </div>

        {/* SMS notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-bold">We've also texted you this link.</p>
            <p>Check the phone number you provided at checkout. Save the text so you can come back to the stream anytime.</p>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-900">
            <p className="font-bold mb-1">Quick tips:</p>
            <ul className="list-disc list-inside space-y-0.5 ml-1">
              <li>Bookmark this page or save the link</li>
              <li>Use the same link to come back on your phone, tablet, or TV</li>
              <li>Lost the link later? Go to <Link to="/recover-ticket" className="underline font-bold">elevatedarena.com/recover-ticket</Link> — we'll text it again</li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <Link to="/events">
            <Button variant="outline" size="lg" className="rounded-full px-8">
              Continue to Events Page
            </Button>
          </Link>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketSuccessPage;
